import { t } from "@lingui/macro";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import {
  Alert, Box, Button, Chip, FormControl, Grid, InputLabel, LinearProgress,
  MenuItem, Paper, Select, Stack, TextField, Typography,
} from "@mui/material";
import axios from "axios";
import { useState } from "react";
import { useUser } from "@/client/services/user";
import { ExecutionResultPanel, LANGUAGES_LIST, PALETTE, SectionPaper } from "../components/shared";
import { DEFAULT_JUDGE0_LANGUAGE } from "../utils/judge0Languages";

const API = "/api/integration/external-service/coding-platform/compiler/run";
const unwrap = (res: any) => res?.data?.result ?? res?.data;

export default function CompilerView() {
  const { user } = useUser();
  const studentId = user?.id ?? "";
  const [language, setLanguage] = useState(DEFAULT_JUDGE0_LANGUAGE);
  const [code, setCode] = useState(DEFAULT_JUDGE0_LANGUAGE.boilerplate);
  const [stdin, setStdin] = useState("");
  const [expectedOutput, setExpectedOutput] = useState("");
  const [running, setRunning] = useState(false);
  const [execution, setExecution] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const onLanguageChange = (value: string) => {
    const next = LANGUAGES_LIST.find((l) => l.value === value) ?? DEFAULT_JUDGE0_LANGUAGE;
    setLanguage(next);
    setCode(next.boilerplate);
    setExecution(null);
    setError(null);
  };

  const runCode = async () => {
    if (!code.trim()) {
      setError("Please write some code before running.");
      return;
    }
    setRunning(true);
    setExecution(null);
    setError(null);
    try {
      const res = await axios.post(API, {
        studentId,
        code,
        programmingLanguage: language.key ?? language.name,
        programmingLanguageId: language.id,
        stdin,
        expectedOutput,
      });
      setExecution(unwrap(res));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Failed to run code.");
    } finally {
      setRunning(false);
    }
  };

  const outputMatches = execution?.passed;
  const hasExpected = Boolean(expectedOutput.trim());

  return (
    <Box sx={{ fontSize: 13, maxWidth: 1320, mx: "auto" }}>
      <SectionPaper sx={{ mb: 1.25, overflow: "hidden" }}>
        <Box sx={{ px: 1.75, py: 1.25, borderBottom: `1px solid ${PALETTE.border}`, bgcolor: PALETTE.bgLight }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TerminalOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 800, color: "primary.main", lineHeight: 1.2 }}>
                {t`Online Compiler`}
              </Typography>
              <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary }}>
                {t`Write code, provide input and expected output, then run to verify your program.`}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mb: 1.25 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ fontSize: 12 }}>{t`Language`}</InputLabel>
              <Select
                label={t`Language`}
                value={language.value}
                onChange={(e) => onLanguageChange(e.target.value)}
                sx={{ fontSize: 12 }}
                MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
              >
                {LANGUAGES_LIST.map((lang) => (
                  <MenuItem key={lang.value} value={lang.value} sx={{ fontSize: 12 }}>{lang.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={running}
              onClick={runCode}
              sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, borderRadius: "8px", px: 2, alignSelf: { xs: "stretch", sm: "auto" } }}
            >
              {running ? t`Running...` : t`Run Code`}
            </Button>
          </Stack>

          <Grid container spacing={1.25}>
            <Grid item xs={12} lg={7}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: PALETTE.textSecondary, mb: 0.5 }}>{t`Source Code`}</Typography>
              <TextField
                multiline
                minRows={18}
                maxRows={28}
                fullWidth
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t`Write your code here...`}
                slotProps={{
                  input: {
                    sx: {
                      fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                      fontSize: 13,
                      lineHeight: 1.5,
                      bgcolor: "#0f172a",
                      color: "#e2e8f0",
                      borderRadius: "10px",
                    },
                  },
                }}
                sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: "#334155" } }}
              />
            </Grid>

            <Grid item xs={12} lg={5}>
              <Stack spacing={1.25} sx={{ height: "100%" }}>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: PALETTE.textSecondary, mb: 0.5 }}>{t`Input (stdin)`}</Typography>
                  <TextField
                    multiline
                    minRows={6}
                    fullWidth
                    value={stdin}
                    onChange={(e) => setStdin(e.target.value)}
                    placeholder={t`Enter program input...`}
                    slotProps={{ input: { sx: { fontFamily: "monospace", fontSize: 12, borderRadius: "10px" } } }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: PALETTE.textSecondary, mb: 0.5 }}>{t`Expected Output`}</Typography>
                  <TextField
                    multiline
                    minRows={6}
                    fullWidth
                    value={expectedOutput}
                    onChange={(e) => setExpectedOutput(e.target.value)}
                    placeholder={t`Optional — leave empty to only view program output`}
                    slotProps={{ input: { sx: { fontFamily: "monospace", fontSize: 12, borderRadius: "10px" } } }}
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </SectionPaper>

      {error && (
        <Alert severity="error" sx={{ mb: 1.25, fontSize: 13 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {running && <LinearProgress sx={{ mb: 1.25, borderRadius: 1 }} />}

      {execution && (
        <Box>
          {hasExpected && (
            <Paper sx={{ p: 1.25, mb: 1.25, borderRadius: "12px", border: `1px solid ${PALETTE.border}` }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{t`Output Match:`}</Typography>
                <Chip
                  size="small"
                  label={outputMatches ? t`Matched` : t`Did Not Match`}
                  sx={{
                    fontSize: 11,
                    height: 24,
                    fontWeight: 700,
                    bgcolor: outputMatches ? "#f0fdf4" : "#fef2f2",
                    color: outputMatches ? "#16a34a" : "#dc2626",
                  }}
                />
              </Stack>
            </Paper>
          )}
          <ExecutionResultPanel execution={execution} />
        </Box>
      )}
    </Box>
  );
}
